import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api';

const money = (v) =>
  v == null ? null : `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Accounts present in the custodian feed that are neither mapped nor dismissed.
 *
 * The feed-check error (feedError) is deliberately distinct from the empty
 * state: a failed check rendering as "no new accounts" would be a false
 * all-clear, and the PM would have no way to tell a quiet week from a broken
 * query. feedError is kept separate from action errors (confirm/dismiss/
 * restore/loading dismissed accounts) because those failures don't mean the
 * count is unknown - only a failed feed check does, so only feedError carries
 * that caveat and drives onCountChange(null).
 */
const PendingAccounts = ({ fetchWithAuth, darkMode, onCountChange }) => {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [feedError, setFeedError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDismissed, setShowDismissed] = useState(false);
  const [dismissed, setDismissed] = useState([]);
  const [dismissedError, setDismissedError] = useState(null);

  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const input = `px-2 py-1 rounded border text-sm ${
    darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`;

  const load = useCallback(async () => {
    setLoading(true);
    setFeedError(null);
    setActionError(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.pendingAccounts);
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || `Feed check failed (${res.status})`);
      }
      const data = await res.json();
      setRows(data.accounts || []);
      if (onCountChange) onCountChange(data.count ?? 0);
    } catch (e) {
      setFeedError(e.message || 'Could not check the feed');
      setRows([]);
      if (onCountChange) onCountChange(null);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, onCountChange]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showDismissed) return;
    setDismissedError(null);
    fetchWithAuth(API_ENDPOINTS.manual.dismissedAccounts)
      .then((r) => {
        if (!r.ok) throw new Error(`Could not load dismissed accounts (${r.status})`);
        return r.json();
      })
      .then(setDismissed)
      .catch((e) => setDismissedError(e.message || 'Could not load dismissed accounts'));
  }, [showDismissed, fetchWithAuth]);

  const valueOf = (r, field) =>
    edits[r.account_number]?.[field] ?? r[`suggested_${field}`] ?? '';

  const setField = (acct, field, v) =>
    setEdits((p) => ({ ...p, [acct]: { ...(p[acct] || {}), [field]: v } }));

  const confirm = async (r) => {
    setBusy(r.account_number);
    setActionError(null);
    try {
      const res = await fetchWithAuth(
        API_ENDPOINTS.manual.confirmPendingAccount(r.account_number),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            codename: valueOf(r, 'codename'),
            name: valueOf(r, 'name'),
          }),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || `Confirm failed (${res.status})`);
      }
      await load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (r) => {
    setBusy(r.account_number);
    setActionError(null);
    try {
      const res = await fetchWithAuth(
        API_ENDPOINTS.manual.dismissPendingAccount(r.account_number),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || `Dismiss failed (${res.status})`);
      }
      await load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const restore = async (acct) => {
    setBusy(acct);
    setActionError(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.dismissPendingAccount(acct), {
        method: 'DELETE',
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || `Restore failed (${res.status})`);
      }
      setShowDismissed(false);
      await load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const statusText = loading
    ? 'Checking the feed...'
    : feedError
      ? 'Pending count unknown'
      : `${rows.length} pending`;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className={`text-sm ${muted}`}>{statusText}</span>
        <button
          onClick={load}
          className={`flex items-center gap-1 text-sm ${muted} hover:underline`}
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {feedError && (
        <div
          role="alert"
          className="flex items-center gap-2 p-2 mb-3 rounded bg-amber-100 text-amber-900 text-sm"
        >
          <AlertTriangle className="w-4 h-4" />
          {feedError} - the pending count is unknown, not zero.
        </div>
      )}
      {actionError && (
        <div role="alert" className="p-2 mb-3 rounded bg-red-100 text-red-800 text-sm">
          {actionError}
        </div>
      )}
      {!loading && !feedError && rows.length === 0 && (
        <div className={`text-sm ${muted}`}>No new accounts waiting.</div>
      )}

      <div className="overflow-x-auto max-h-[60vh]">
        {rows.map((r) => (
          <div
            key={r.account_number}
            className={`p-3 rounded border mb-2 ${
              darkMode ? 'border-gray-700' : 'border-gray-200'}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-medium">{r.account_number}</span>
              <span className={`text-xs ${muted}`}>
                {r.shortname} - {r.portfolio_type} - {r.tax_status} - first seen {r.first_seen}
              </span>
            </div>
            <div className={`text-xs mt-1 ${muted}`}>
              {r.position_count} positions
              {r.market_value != null ? ` - ${money(r.market_value)}` : ' - no holdings'}
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <input
                className={`${input} w-32`}
                aria-label={`Codename for ${r.account_number}`}
                value={valueOf(r, 'codename')}
                onChange={(e) => setField(r.account_number, 'codename', e.target.value)}
              />
              <input
                className={`${input} flex-1 min-w-[14rem]`}
                aria-label={`Name for ${r.account_number}`}
                value={valueOf(r, 'name')}
                onChange={(e) => setField(r.account_number, 'name', e.target.value)}
              />
              <button
                disabled={busy === r.account_number}
                onClick={() => confirm(r)}
                className="flex items-center gap-1 px-3 py-1.5 rounded bg-teal-600 text-white text-sm disabled:opacity-50"
              >
                <Check className="w-4 h-4" /> Confirm
              </button>
              <button
                disabled={busy === r.account_number}
                onClick={() => dismiss(r)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm border disabled:opacity-50 ${
                  darkMode ? 'border-gray-700' : 'border-gray-300'}`}
              >
                <X className="w-4 h-4" /> Dismiss
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <button
          onClick={() => setShowDismissed((v) => !v)}
          className={`text-sm ${muted} hover:underline`}
        >
          {showDismissed ? 'Hide' : 'Show'} dismissed ({dismissed.length})
        </button>
        {showDismissed && (
          <div className="mt-2">
            {dismissedError && (
              <div role="alert" className="p-2 mb-2 rounded bg-red-100 text-red-800 text-sm">
                {dismissedError}
              </div>
            )}
            {dismissed.map((d) => (
              <div
                key={d.account_number}
                className={`flex items-center gap-2 p-2 rounded border mb-1 text-sm ${
                  darkMode ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <span className="font-mono">{d.account_number}</span>
                <span className={muted}>{d.dismissed_at}</span>
                {d.reason && <span className={muted}>- {d.reason}</span>}
                <button
                  disabled={busy === d.account_number}
                  onClick={() => restore(d.account_number)}
                  className="ml-auto text-teal-600 hover:underline disabled:opacity-50"
                >
                  Restore
                </button>
              </div>
            ))}
            {dismissed.length === 0 && !dismissedError && (
              <div className={`text-sm ${muted}`}>No dismissed accounts.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingAccounts;
